import { useState, useEffect, useCallback } from 'react';

interface NetworkState {
  online: boolean;
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
}

// Extend Navigator interface for network information
declare global {
  interface Navigator {
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      addEventListener: (type: string, listener: () => void) => void;
      removeEventListener: (type: string, listener: () => void) => void;
    };
  }
}

export function useNetworkStatus() {
  const [networkState, setNetworkState] = useState<NetworkState>({
    online: navigator.onLine,
    effectiveType: null,
    downlink: null,
    rtt: null,
  });

  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setNetworkState((prev) => ({ ...prev, online: true }));
      setWasOffline(true);
      // Reset after a delay
      setTimeout(() => setWasOffline(false), 5000);
    };

    const handleOffline = () => {
      setNetworkState((prev) => ({ ...prev, online: false }));
    };

    // Network Information API (if available)
    const connection = navigator.connection;

    const updateConnectionStatus = () => {
      if (connection) {
        setNetworkState((prev) => ({
          ...prev,
          effectiveType: connection.effectiveType || null,
          downlink: connection.downlink || null,
          rtt: connection.rtt || null,
        }));
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (connection) {
      connection.addEventListener('change', updateConnectionStatus);
      updateConnectionStatus();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection) {
        connection.removeEventListener('change', updateConnectionStatus);
      }
    };
  }, []);

  const checkOnline = useCallback(async (): Promise<boolean> => {
    if (!navigator.onLine) return false;

    try {
      // Try to fetch a small resource to verify actual connectivity
      const response = await fetch('/api/health', {
        method: 'HEAD',
        cache: 'no-store',
      });
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  return {
    ...networkState,
    wasOffline,
    checkOnline,
    isSlowConnection: networkState.effectiveType === '2g' || networkState.effectiveType === 'slow-2g',
  };
}

// Hook for background sync
export function useBackgroundSync() {
  const [isSyncing, setIsSyncing] = useState(false);

  const sync = useCallback(async (tag: string): Promise<boolean> => {
    if (!('serviceWorker' in navigator) || !('SyncManager' in window)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      // @ts-expect-error - SyncManager not in all TypeScript definitions
      await registration.sync.register(tag);
      return true;
    } catch (error) {
      console.error('Background sync registration failed:', error);
      return false;
    }
  }, []);

  const syncMessages = useCallback(async (): Promise<boolean> => {
    setIsSyncing(true);
    const result = await sync('sync-messages');
    setIsSyncing(false);
    return result;
  }, [sync]);

  return {
    sync,
    syncMessages,
    isSyncing,
  };
}

export default useNetworkStatus;
